import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  CircularProgress,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Tooltip,
  Stack,
  Card,
  CardContent,
  Switch,
  TablePagination,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Business as BusinessIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';

const OrganizationManager = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    subdomain: '',
    timezone: 'America/Toronto',
    contact_email: '',
    phone_number: '',
    address: '',
    is_active: true
  });

  // Check if user is system_admin
  useEffect(() => {
    if (user && user.role !== 'system_admin') {
      const fallback = user.organization_slug
        ? `/${user.organization_slug}/dashboard`
        : '/not-found';
      navigate(fallback, {
        replace: true,
        state: { error: 'Access denied. System Admin privileges required.' },
      });
    }
  }, [user, navigate]);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch all organizations (including inactive for admin view)
      // Note: Public endpoint only returns active ones, so we'll need to call individual endpoints
      // or create an admin endpoint. For now, we'll fetch active and handle deactivated separately
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3003'}/api/organizations`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setOrganizations(response.data.organizations || []);
      setError('');
    } catch (err) {
      console.error('Error fetching organizations:', err);
      setError(err.response?.data?.error || 'Failed to fetch organizations');
      if (err.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const handleCreate = async () => {
    try {
      setError('');
      const token = localStorage.getItem('token');
      
      // Validate slug format (lowercase, alphanumeric, hyphens only)
      const slugRegex = /^[a-z0-9-]+$/;
      if (!slugRegex.test(formData.slug)) {
        setError('Slug must contain only lowercase letters, numbers, and hyphens');
        return;
      }

      // Remove is_active from payload - backend sets it to true by default
      const { is_active, ...createData } = formData;

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3003'}/api/organizations`,
        createData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess('Organization created successfully!');
      setCreateDialog(false);
      resetForm();
      fetchOrganizations();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error creating organization:', err);
      setError(err.response?.data?.error || 'Failed to create organization');
    }
  };

  const handleEdit = async () => {
    try {
      setError('');
      const token = localStorage.getItem('token');
      
      const slugRegex = /^[a-z0-9-]+$/;
      if (!slugRegex.test(formData.slug)) {
        setError('Slug must contain only lowercase letters, numbers, and hyphens');
        return;
      }

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3003'}/api/organizations/${selectedOrg.id}`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess('Organization updated successfully!');
      setEditDialog(false);
      setSelectedOrg(null);
      resetForm();
      fetchOrganizations();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating organization:', err);
      setError(err.response?.data?.error || 'Failed to update organization');
    }
  };

  const handleDelete = async () => {
    try {
      setError('');
      const token = localStorage.getItem('token');
      
      await axios.delete(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3003'}/api/organizations/${selectedOrg.id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess('Organization deactivated successfully!');
      setDeleteDialog(false);
      setSelectedOrg(null);
      fetchOrganizations();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deactivating organization:', err);
      setError(err.response?.data?.error || 'Failed to deactivate organization');
    }
  };

  const openCreateDialog = () => {
    resetForm();
    setCreateDialog(true);
  };

  const openEditDialog = (org) => {
    setSelectedOrg(org);
    setFormData({
      name: org.name || '',
      slug: org.slug || '',
      subdomain: org.subdomain || '',
      timezone: org.timezone || 'America/Toronto',
      contact_email: org.contact_email || '',
      phone_number: org.phone_number || '',
      address: org.address || '',
      is_active: org.is_active !== false
    });
    setEditDialog(true);
  };

  const openDeleteDialog = (org) => {
    setSelectedOrg(org);
    setDeleteDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      subdomain: '',
      timezone: 'America/Toronto',
      contact_email: '',
      phone_number: '',
      address: '',
      is_active: true
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter organizations based on search
  const filteredOrganizations = organizations.filter(org =>
    org.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.slug?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.contact_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedOrganizations = filteredOrganizations.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading && organizations.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Paper sx={{ p: 3, my: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <BusinessIcon fontSize="large" color="primary" />
            <Typography variant="h4">
              Organization Management
            </Typography>
          </Stack>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
            color="primary"
          >
            Add New Church
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search organizations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell>Subdomain</TableCell>
                <TableCell>Timezone</TableCell>
                <TableCell>Contact Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedOrganizations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      No organizations found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedOrganizations.map((org) => (
                  <TableRow key={org.id} hover>
                    <TableCell>
                      <Typography 
                        variant="body1" 
                        fontWeight="medium"
                        component={Link}
                        to={`/${org.slug}/dashboard`}
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                            color: 'primary.dark'
                          }
                        }}
                      >
                        {org.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={org.slug} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{org.subdomain || '-'}</TableCell>
                    <TableCell>{org.timezone}</TableCell>
                    <TableCell>{org.contact_email || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        icon={org.is_active ? <ActiveIcon /> : <InactiveIcon />}
                        label={org.is_active ? 'Active' : 'Inactive'}
                        color={org.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {org.created_at
                        ? format(new Date(org.created_at), 'MMM dd, yyyy')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Edit Organization">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openEditDialog(org)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={org.is_active ? 'Deactivate' : 'Activate'}>
                          <IconButton
                            size="small"
                            color={org.is_active ? 'error' : 'success'}
                            onClick={() => openDeleteDialog(org)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredOrganizations.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Create Organization Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Church Organization</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Church Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="e.g., Winners Chapel Kitchener"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Slug"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase() })}
                required
                placeholder="e.g., kitchener"
                helperText="URL-friendly identifier (lowercase, no spaces)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Subdomain (Optional)"
                value={formData.subdomain}
                onChange={(e) => setFormData({ ...formData, subdomain: e.target.value.toLowerCase() })}
                placeholder="e.g., kitchener"
                helperText="For future subdomain support"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  label="Timezone"
                >
                  <MenuItem value="America/Toronto">Eastern Time (Toronto)</MenuItem>
                  <MenuItem value="America/Winnipeg">Central Time (Winnipeg)</MenuItem>
                  <MenuItem value="America/Vancouver">Pacific Time (Vancouver)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Email"
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                placeholder="contact@church.wsf.com"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                placeholder="(519) 555-1234"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={2}
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Church physical address"
              />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                <Typography variant="body2">Active (Church is operational)</Typography>
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" color="primary">
            Create Organization
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Organization Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Organization</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Church Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Slug"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase() })}
                required
                helperText="URL-friendly identifier"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Subdomain"
                value={formData.subdomain}
                onChange={(e) => setFormData({ ...formData, subdomain: e.target.value.toLowerCase() })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  label="Timezone"
                >
                  <MenuItem value="America/Toronto">Eastern Time (Toronto)</MenuItem>
                  <MenuItem value="America/Winnipeg">Central Time (Winnipeg)</MenuItem>
                  <MenuItem value="America/Vancouver">Pacific Time (Vancouver)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Email"
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={2}
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                <Typography variant="body2">Active</Typography>
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button onClick={handleEdit} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete/Deactivate Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {selectedOrg?.is_active ? 'deactivate' : 'activate'} the organization{' '}
            <strong>{selectedOrg?.name}</strong>?
          </Typography>
          {selectedOrg?.is_active && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Deactivating will prevent new signups and may affect existing users.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color={selectedOrg?.is_active ? 'error' : 'success'}>
            {selectedOrg?.is_active ? 'Deactivate' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OrganizationManager;

